/** @file Reads one numeric timeline of the document as a curve track in display (absolute) units. */
import { absoluteValue } from '@pose2d/api/queries/absoluteValue';
import type { AnimationDoc, Curve, Doc } from '@pose2d/format/types';
import { CURVE_CHANNELS } from './curveChannels.data';
import type { CurveChannelSpec, CurveRef, CurveTrack } from './curveTypes';

type StoredKey = { f: number; v?: unknown; curve?: Curve | undefined };
type Tracks = Record<string, Record<string, StoredKey[] | undefined> | undefined>;
type Bezier = { bezier: [number, number, number, number] };

/** The stored keys of the timeline (deform: the attachment's own key list). */
function storedKeys(anim: AnimationDoc, spec: CurveChannelSpec, target: string): readonly StoredKey[] {
  if (spec.group === 'deform') return anim.deform[target] ?? [];
  return (anim[spec.group] as Tracks)[target]?.[spec.prop] ?? [];
}

/** The bone's setup value of a setup-relative channel; undefined for channels stored absolute. */
function setupOf(doc: Doc, spec: CurveChannelSpec, ref: CurveRef): number | undefined {
  if (spec.setup === 'none') return undefined;
  const setup = doc.bones[ref.target]?.setup;
  return setup?.[ref.channel as keyof typeof setup];
}

/** Absolute value of a stored key (api absoluteValue: the inverse that round-trips through setKey). */
function displayValue(spec: CurveChannelSpec, setup: number | undefined, v: unknown): number {
  if (spec.mode === 'progress' || typeof v !== 'number') return 0;
  return setup === undefined ? v : absoluteValue(v, setup, spec.setup === 'scale');
}

const isBezier = (curve: Curve | undefined): curve is Bezier => typeof curve === 'object' && 'bezier' in curve;

/** A scale key's bezier dv back in absolute units (setKey/setCurve store it divided by the setup scale). */
function displayCurve(spec: CurveChannelSpec, setup: number | undefined, curve: Curve | undefined): Curve | undefined {
  if (spec.setup !== 'scale' || setup === undefined || !isBezier(curve)) return curve;
  const [x1, d1, x2, d2] = curve.bezier;
  return { bezier: [x1, absoluteValue(d1, setup, true), x2, absoluteValue(d2, setup, true)] };
}

/** The display track of `ref`, or null when the animation is missing or the timeline holds no key. */
export function buildCurveTrack(doc: Doc, ref: CurveRef): CurveTrack | null {
  const anim = doc.animations[ref.animation];
  if (anim === undefined) return null;
  const spec = CURVE_CHANNELS[ref.channel];
  const keys = storedKeys(anim, spec, ref.target);
  if (keys.length === 0) return null;
  const setup = setupOf(doc, spec, ref);
  return {
    ref,
    mode: spec.mode,
    frames: keys.map((k) => k.f),
    values: keys.map((k) => displayValue(spec, setup, k.v)),
    curves: keys.map((k) => displayCurve(spec, setup, k.curve)),
  };
}
